// Add TypeScript interface for gtag
declare global {
  interface Window {
    gtag: (...args: any[]) => void
    promotekit_referral?: string
    dataLayer: any[]
  }
}
// Helper to check if gtag is available
const isGtagAvailable = () => {
  try {
    return typeof window !== 'undefined' && typeof window.gtag === 'function'
  } catch (e) {
    return false
  }
}
// Safe tracking wrapper with better error handling
const safeTrack = (trackingCall: () => void): boolean => {
  try {
    if (typeof window === 'undefined') return false
    // Only execute if gtag is available
    if (!isGtagAvailable()) {
      return false
    }
    trackingCall()
    return true
  } catch (error) {
    // Silently fail for cross-origin errors
    return false
  }
}
// Initialize tracking with better error handling and CORS support
export const initializeTracking = (): boolean => {
  try {
    if (typeof window === 'undefined') return false
    // Initialize dataLayer if not exists
    window.dataLayer = window.dataLayer || []
    window.gtag = function () {
      window.dataLayer.push(arguments)
    }
    // Only initialize if not already done
    if (!isGtagAvailable()) {
      // Create script with proper attributes
      const script = document.createElement('script')
      script.async = true
      script.crossOrigin = 'anonymous' // Add CORS attribute
      script.src = 'https://www.googletagmanager.com/gtag/js?id=G-M50P4LPNWG'
      // Add load handler with error catching
      // script.onload = () => {
      //   try {
      //     window.gtag('js', new Date())
      //     // window.gtag('config', 'G-M50P4LPNWG', {
      //     //   send_page_view: false,
      //     // })
      //     gtag('config', 'G-M50P4LPNWG');
      //   } catch (error) {
      //     console.warn('Error initializing gtag:', error)
      //   }
      // }
      // Enhanced error handler
      script.onerror = (error) => {
        console.warn('Failed to load Google Analytics:', error)
        // Remove failed script
        if (script.parentNode) {
          script.parentNode.removeChild(script)
        }
        return false
      }
      // Add script to head with error catching
      try {
        document.head.appendChild(script)
      } catch (error) {
        console.warn('Error appending GA script:', error)
        return false
      }
    }

    if (window.gtag) {
      window.gtag('js', new Date())
      window.gtag('config', 'G-M50P4LPNWG')
    }
    
    return true
  } catch (error) {
    console.warn('Error initializing tracking:', error)
    return false
  }
}
// Check if tracking is initialized
export const isTrackingInitialized = () => {
  return isGtagAvailable()
}
// Track page views
export const trackPageView = (path: string) => {
  return safeTrack(() => {
    if (!window.gtag) return
    window.gtag('event', 'page_view', {
      page_location: window.location.href,
      page_path: path,
      page_title: document.title,
      send_to: 'G-M50P4LPNWG',
    })
  })
}
// Track events
export const trackEvent = (
  category: string,
  action: string,
  label?: string,
  value?: number,
) => {
  return safeTrack(() => {
    if (!window.gtag) return
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
      send_to: 'G-M50P4LPNWG',
    })
  })
}
// Initialize referral tracking
export const initializeReferralTracking = () => {
  try {
    if (typeof window === 'undefined') return
    // Check if script is already loaded
    const existingScript = document.querySelector(
      'script[src*="promotekit.js"]',
    )
    if (existingScript) {
      // If script exists, just update links
      setTimeout(updateReferralLinks, 1500)
      return
    }
    const script = document.createElement('script')
    script.async = true
    script.crossOrigin = 'anonymous'
    script.src = 'https://cdn.promotekit.com/promotekit.js'
    script.setAttribute(
      'data-promotekit',
      '20fab87b-f678-4888-a763-ebb32cc937ff',
    )
    // Enhanced error handler
    const handleError = (error: ErrorEvent) => {
      console.warn('PromoteKit script failed to load:', error)
      if (document.head.contains(script)) {
        document.head.removeChild(script)
      }
    }
    // Enhanced load handler with retry mechanism
    const handleLoad = () => {
      try {
        if (window.promotekit_referral) {
          updateReferralLinks()
        } else {
          // Retry a few times if promotekit_referral isn't set immediately
          let retries = 0
          const checkInterval = setInterval(() => {
            if (window.promotekit_referral || retries >= 3) {
              updateReferralLinks()
              clearInterval(checkInterval)
            }
            retries++
          }, 500)
        }
      } catch (error) {
        console.warn('Error in PromoteKit load handler:', error)
      }
    }
    script.addEventListener('error', handleError)
    script.addEventListener('load', handleLoad)
    try {
      document.head.appendChild(script)
    } catch (error) {
      console.warn('Error appending PromoteKit script:', error)
      return () => {}
    }
    // Return cleanup function
    return () => {
      script.removeEventListener('error', handleError)
      script.removeEventListener('load', handleLoad)
      if (document.head.contains(script)) {
        document.head.removeChild(script)
      }
    }
  } catch (error) {
    console.warn('Error initializing referral tracking:', error)
    return () => {}
  }
}
// Helper function to update referral links
const updateReferralLinks = () => {
  try {
    if (typeof window === 'undefined' || !window.promotekit_referral) return
    // Update Stripe links
    document
      .querySelectorAll('a[href^="https://buy.stripe.com/"]')
      .forEach((link) => {
        const oldBuyUrl = link.getAttribute('href')
        if (!oldBuyUrl || oldBuyUrl.includes('client_reference_id')) return
        const newBuyUrl = `${oldBuyUrl}?client_reference_id=${window.promotekit_referral}`
        link.setAttribute('href', newBuyUrl)
      })
    // Update Stripe elements
    document.querySelectorAll('[buy-button-id]').forEach((element) => {
      if (window.promotekit_referral) {
        element.setAttribute('client-reference-id', window.promotekit_referral)
      }
    })
  } catch (error) {
    // Silently fail for cross-origin errors
  }
}
